import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { imgUpload } from "../../asset";

interface DocumentUploadProps {
  /* ===== Callbacks ===== */
  onUploadComplete?: (url: string) => void; // normal upload
  onFileSelect?: (file: File) => void; // excel / csv / local file

  /* ===== UI / Config ===== */
  label?: React.ReactNode;
  folder?: string;
  className?: string;
  value?: string;
  placeholderImage?: string;
  borderStyle?: "border-dashed" | "solid";
  buttonText?: string;
  buttonWidth?: "full" | "fit";
  buttonAlign?: "left" | "center" | "right";
  disabled?: boolean;
  buttonColor?: string;
  showButton?: boolean;
  showDropzone?: boolean;
  text?: boolean;
  accept?: string;
}

export default function DocumentUpload({
  label,
  onUploadComplete,
  onFileSelect,
  folder = "school_mgmt",
  className = "",
  value,
  placeholderImage = imgUpload,
  borderStyle = "border-dashed",
  buttonText = "Choose Document",
  buttonWidth = "full",
  buttonAlign = "center",
  disabled = false,
  buttonColor = "bg-primary-500",
  showButton = true,
  showDropzone = true,
  text = false,
  accept = ".pdf,.doc,.docx,.xls,.xlsx,.png",
}: DocumentUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  /* ---------------- FILE CHANGE ---------------- */
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    const file = e.target.files?.[0];
    if (!file) return;

    // 🔹 Excel / CSV / local file mode
    if (onFileSelect) {
      onFileSelect(file);
      if (inputRef.current) inputRef.current.value = "";
      return;
    }

    // 🔹 Normal upload mode
    await uploadFile(file);
  };

  /* ---------------- UPLOAD TO SERVER ---------------- */
  const uploadFile = async (file: File) => {
    if (!onUploadComplete) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("folder", folder);

    try {
      setIsUploading(true);
      toast.loading("Uploading...", { id: "upload" });

      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL_IMAGE}api/file-upload`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!res.ok) {
        const errorText = await res.text();
        toast.error(errorText, { id: "upload" });
        return;
      }

      const data = await res.json();
      if (data?.file) {
        onUploadComplete(data.file);
        toast.success("Upload successful!", { id: "upload" });
        if (inputRef.current) inputRef.current.value = "";
      } else {
        toast.error("Upload failed.", { id: "upload" });
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Unknown upload error.";
      toast.error(errorMessage, { id: "upload" });
    } finally {
      setIsUploading(false);
    }
  };

  /* ---------------- CLICK / DROP ---------------- */
  const handleClick = () => {
    if (!disabled) inputRef.current?.click();
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (disabled) return;

    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    if (onFileSelect) {
      onFileSelect(file);
      return;
    }

    await uploadFile(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  /* ---------------- UI HELPERS ---------------- */
  const alignClass =
    buttonAlign === "left"
      ? "justify-start"
      : buttonAlign === "right"
      ? "justify-end"
      : "justify-center";

  const isImage = (url?: string) =>
    !!url && /\.(jpg|jpeg|png|gif|webp)$/i.test(url);

  const fileName = value ? value.split("/").pop() : "";

  /* ---------------- RENDER ---------------- */
  return (
    <div className={`relative group ${className}`}>
      {label && (
        <span className="text-sm bg-white px-1 mb-2 text-neutral-400 font-semibold">
          {label}
        </span>
      )}

      <input
        type="file"
        accept={accept}
        ref={inputRef}
        onChange={handleFileChange}
        className="hidden"
      />

      {showDropzone && (
        <div
          className={`w-full h-full rounded-xl py-1 cursor-pointer transition-all relative overflow-hidden border-2 ${
            isUploading
              ? "border-primary-500 bg-blue-50"
              : "border-neutral-100 hover:border-primary-500"
          } ${borderStyle}`}
          onClick={handleClick}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          {isUploading ? (
            <div className="flex flex-col items-center justify-center h-full text-primary-500">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500 mb-3"></div>
              <span className="text-sm font-medium">Uploading...</span>
            </div>
          ) : value ? (
            <div className="flex flex-col items-center justify-center h-full gap-2 px-3 text-center">
              {isImage(value) ? (
                <img
                  src={value}
                  alt="preview"
                  className="max-h-40 rounded-md object-contain"
                />
              ) : (
                <>
                  <div className="text-4xl">📄</div>
                  <p className="text-sm text-neutral-500 truncate max-w-[200px]">
                    {fileName}
                  </p>
                  <a
                    href={value}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-500 text-sm underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    View Document
                  </a>
                </>
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-3 items-center justify-center h-full">
              <img src={placeholderImage} alt="upload" />
              {text && (
                <p className="text-neutral-400 text-lg text-center">
                  Drag and drop files here, or click to upload.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {showButton && (
        <div className={`flex ${alignClass}`}>
          <button
            type="button"
            onClick={handleClick}
            disabled={disabled}
            className={`rounded-lg capitalize mt-2 py-[9.5px] px-6 text-sm font-semibold text-white ${
              buttonWidth === "full" ? "w-full" : "w-fit"
            } ${disabled ? "bg-primary-200 cursor-not-allowed" : buttonColor}`}
          >
            {value ? "Change Document" : buttonText}
          </button>
        </div>
      )}
    </div>
  );
}
